import React, { useState } from "react";
import { s } from "../lib/theme";
import { fmt, HOURS_PER_DAY, todayStr } from "../lib/pto";

export default function PtoReport({ requests, users }) {
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));

  function hoursInWindow(r) {
    const rs = new Date(r.start + "T00:00:00");
    const re = new Date(r.end + "T00:00:00");
    const ps = new Date(start + "T00:00:00");
    const pe = new Date(end + "T00:00:00");
    const from = rs > ps ? rs : ps;
    const to = re < pe ? re : pe;
    if (to < from) return { days: 0, hours: 0 };
    let days = 0;
    const d = new Date(from);
    while (d <= to) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) days++;
      d.setDate(d.getDate() + 1);
    }
    return { days, hours: r.hours > 0 ? days * HOURS_PER_DAY : 0 };
  }

  const approved = requests.filter((r) => r.status === "approved");
  const rows = approved
    .map((r) => ({ ...r, ...hoursInWindow(r) }))
    .filter((r) => r.days > 0)
    .sort((a, b) => a.user_name.localeCompare(b.user_name) || new Date(a.start) - new Date(b.start));

  const ptoRows = rows.filter((r) => r.hours > 0);
  const otherRows = rows.filter((r) => r.hours === 0);
  const totalPtoHours = ptoRows.reduce((s2, r) => s2 + r.hours, 0);
  const employees = users.filter((u) => u.role !== "admin" && u.active !== false).sort((a, b) => a.name.localeCompare(b.name));

  const periodLabel = `${fmt(start)} – ${fmt(end)}`;

  return (
    <div>
      <div style={s.card} className="no-print">
        <div style={s.cardTitle}>Generate PTO Report</div>
        <div style={{ ...s.muted, marginBottom: 12 }}>
          Choose the pay period. The report lists approved PTO by the dates actually taken within
          the period, so it lands on the correct paycheck. Print or save as PDF and send to payroll.
        </div>
        <div style={s.row2}>
          <div>
            <label style={s.label}>Pay period start</label>
            <input data-testid="report-start" style={s.input} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Pay period end</label>
            <input data-testid="report-end" style={s.input} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <button data-testid="report-print-btn" style={s.btnPrimary} onClick={() => window.print()}>
          Print / Save as PDF
        </button>
      </div>

      <div style={s.reportSheet} id="pto-report">
        <div style={s.reportHead}>
          <div style={s.reportBrand}>Tennessee Implant &amp; Oral Surgery</div>
          <div style={s.reportTitle}>PTO Report for Payroll</div>
          <div style={s.reportPeriod}>Pay period: {periodLabel}</div>
          <div style={s.reportGen}>Generated {fmt(todayStr())} by Dr. Kirk</div>
        </div>

        <div style={s.reportSection}>Paid PTO Taken This Period</div>
        {ptoRows.length === 0 && <div style={s.muted}>No PTO taken in this period.</div>}
        {ptoRows.length > 0 && (
          <table style={s.reportTable}>
            <thead>
              <tr>
                <th style={s.rth}>Employee</th>
                <th style={s.rth}>Type</th>
                <th style={s.rth}>Dates</th>
                <th style={{ ...s.rth, textAlign: "right" }}>Days</th>
                <th style={{ ...s.rth, textAlign: "right" }}>Hours</th>
              </tr>
            </thead>
            <tbody>
              {ptoRows.map((r) => (
                <tr key={r.id}>
                  <td style={s.rtd}>{r.user_name}</td>
                  <td style={s.rtd}>{r.type_label}</td>
                  <td style={s.rtd}>{fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""}</td>
                  <td style={{ ...s.rtd, textAlign: "right" }}>{r.days}</td>
                  <td style={{ ...s.rtd, textAlign: "right" }}>{r.hours}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...s.rtd, fontWeight: 700 }} colSpan={4}>Total paid PTO hours</td>
                <td style={{ ...s.rtd, textAlign: "right", fontWeight: 700 }}>{totalPtoHours}</td>
              </tr>
            </tbody>
          </table>
        )}

        {otherRows.length > 0 && (
          <>
            <div style={s.reportSection}>Other Approved Leave (not paid from PTO)</div>
            <table style={s.reportTable}>
              <thead>
                <tr>
                  <th style={s.rth}>Employee</th>
                  <th style={s.rth}>Type</th>
                  <th style={s.rth}>Dates</th>
                  <th style={{ ...s.rth, textAlign: "right" }}>Days</th>
                </tr>
              </thead>
              <tbody>
                {otherRows.map((r) => (
                  <tr key={r.id}>
                    <td style={s.rtd}>{r.user_name}</td>
                    <td style={s.rtd}>{r.type_label}</td>
                    <td style={s.rtd}>{fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""}</td>
                    <td style={{ ...s.rtd, textAlign: "right" }}>{r.days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div style={s.reportSection}>Current PTO Balances (for reconciliation)</div>
        <table style={s.reportTable}>
          <thead>
            <tr>
              <th style={s.rth}>Employee</th>
              <th style={{ ...s.rth, textAlign: "right" }}>PTO balance (hrs)</th>
              <th style={{ ...s.rth, textAlign: "right" }}>≈ days</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((u) => (
              <tr key={u.id}>
                <td style={s.rtd}>{u.name}</td>
                <td style={{ ...s.rtd, textAlign: "right" }}>{u.balance_hours}</td>
                <td style={{ ...s.rtd, textAlign: "right" }}>{(u.balance_hours / HOURS_PER_DAY).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={s.reportNote}>
          Balances reflect the tracker as of the generated date. Payroll remains the official record;
          please reconcile and advise of any discrepancy.
        </div>
      </div>
    </div>
  );
}
